import React, { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";

import { useTransaction } from "../../../contexts/TransactionContext";
import { useGetPayoutConfig } from "../../../services/queries/wallets";
import useDebounce from "../../../hooks/useDebounce";
import { WithdrawalType, routePaths } from "../../../constants";

import { LocalWithdrawalInput } from "../LocalWithdrawalInput";
import { LocalFeeDisplay } from "../LocalFeeDisplay";

function LocalWithdrawalForm() {
  const navigate = useNavigate();
  const [error, setError] = useState<string>("");

  const { state: formData, updateTransaction } = useTransaction();
  const { baseCurrency, baseAmount, fee, remarks, balance } = formData;

  // Debounce the amount for fee fetching (500ms for better responsiveness)
  const debouncedAmount = useDebounce(baseAmount, 500);

  const { data: payoutConfigData, isLoading: isLoadingPayoutConfig } =
    useGetPayoutConfig(debouncedAmount, baseCurrency);

  const payoutConfig = payoutConfigData?.data;
  const withdrawalFee = payoutConfig?.fee;
  const minimumAmount = payoutConfig?.minimumAmount ?? 0;

  // Update fee in form data when fetched
  useEffect(() => {
    if (withdrawalFee !== undefined) {
      updateTransaction({ fee: withdrawalFee });
    }
  }, [withdrawalFee, updateTransaction]);

  // Validation
  useEffect(() => {
    setError("");

    if (!baseAmount) return;

    if (minimumAmount > 0 && Number(baseAmount) < minimumAmount) {
      setError(
        `Minimum amount is ${minimumAmount.toLocaleString(
          "en-US",
        )} ${baseCurrency}`,
      );
      return;
    }

    const totalAmount = Number(baseAmount) + Number(fee ?? 0);

    if (totalAmount > balance) {
      setError(
        `Total amount (${totalAmount.toLocaleString(
          "en-US",
        )} ${baseCurrency}) exceeds your balance of ${balance.toLocaleString(
          "en-US",
        )} ${baseCurrency}`,
      );
      return;
    }
  }, [baseAmount, debouncedAmount, minimumAmount, baseCurrency, fee, balance]);

  const handleBaseAmountChange = (amount: string) => {
    updateTransaction({ baseAmount: amount });
  };

  const handleRemarksChange = (remarks: string) => {
    updateTransaction({ remarks });
  };

  const handleSubmit = () => {
    updateTransaction({
      withdrawalType: WithdrawalType.LOCAL,
    });

    navigate({ to: routePaths.beneficiary });
  };

  const isInValidWithdrawalForm =
    !baseAmount || isLoadingPayoutConfig || fee === undefined || error !== "";

  return (
    <div className="local-withdrawal-container space-y-5">
      {/* Amount Input with Formatting */}
      <LocalWithdrawalInput
        currency={baseCurrency}
        amount={baseAmount}
        balance={balance}
        onAmountChange={handleBaseAmountChange}
        error={error}
        isLoading={isLoadingPayoutConfig}
      />

      {/* Fee Display with Breakdown */}
      <LocalFeeDisplay
        amount={baseAmount}
        currency={baseCurrency}
        fee={fee ?? 0}
        isLoading={isLoadingPayoutConfig}
      />

      {/* Remarks Input */}
      <div className="remarks-container bg-white rounded-lg p-4 relative border border-gray-200">
        <input
          type="text"
          placeholder="Add Note"
          value={remarks}
          onChange={(e) => handleRemarksChange(e.target.value)}
          className="w-full focus:outline-none text-gray-500"
          maxLength={18}
        />
        <div className="text-right text-xs text-gray-400 absolute bottom-2 right-2">
          {remarks?.length || 0}/18
        </div>
      </div>

      {/* Submit Button */}
      <button
        disabled={isInValidWithdrawalForm}
        className={`w-full bg-primary text-white py-4 px-6 rounded-full font-medium ${
          isInValidWithdrawalForm
            ? "opacity-50 cursor-not-allowed"
            : "hover:bg-primary/90"
        }`}
        onClick={handleSubmit}
      >
        Continue
      </button>
    </div>
  );
}

export default React.memo(LocalWithdrawalForm);
